import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  listNotifications,
  useDeleteNotification,
  useMarkNotificationsRead,
} from "@vega-api/generated/notifications-v1/notifications-v1";
import { type ApiResponseUnreadCount } from "@vega-api/generated/types/apiResponseUnreadCount";
import { type MarkNotificationsReadRequestFilter } from "@vega-api/generated/types/markNotificationsReadRequestFilter";
import { type NotificationResponse } from "@vega-api/generated/types/notificationResponse";
import { type NotificationCenterItem } from "@vega-core/components/NotificationCenter/NotificationCenter.types";
import { useToast } from "@vega-core/components/Toast/useToast";

import { NOTIFICATIONS_PAGE_SIZE } from "./constants";
import { dispatchNotification } from "./dispatchNotification";
import { getNotificationIcon } from "./getNotificationIcon";
import { type NotificationExtension } from "./notificationExtension";
import {
  type NotificationsListInfinite,
  updateAllPages,
} from "./notificationsCache";
import {
  getPublisherUnreadCountQueryKey,
  getScopedListParams,
  getScopedListQueryKey,
  invalidateAllNotifications,
  type NotificationScope,
} from "./notificationScope";
import { useNotificationsPatchMutation } from "./useNotificationsPatchMutation";

const MIN_RETRY_FEEDBACK_MS = 800;

const toCenterItem = (
  notification: NotificationResponse,
  extension?: NotificationExtension,
): NotificationCenterItem<NotificationResponse> => ({
  id: notification.id,
  title: notification.title,
  body: notification.body,
  createdAt: notification.createdAt,
  readAt: notification.readAt ?? null,
  icon:
    extension?.getIcon?.(notification.type) ??
    getNotificationIcon(notification.type),
  meta: notification,
});

// Surfaces that mark only what's loaded pass `readVisibleItemsOnly` (filter by
// id); otherwise everything in scope is marked via an empty filter.
const buildMarkAllReadFilter = (
  readVisibleItemsOnly: boolean,
  loadedIds: string[],
): MarkNotificationsReadRequestFilter =>
  readVisibleItemsOnly ? { ids: loadedIds } : {};

type Options = {
  enabled?: boolean;
  readVisibleItemsOnly?: boolean;
  extension?: NotificationExtension;
};

/**
 * Data layer for the Notification Centre, scoped by `scope`. Returns the props
 * for `<NotificationCenter>`. An empty scope reproduces today's GP behaviour;
 * co-invest surfaces pass `{ publisher, metadata? }`.
 */
export const useNotificationCenterData = (
  scope: NotificationScope = {},
  { enabled = true, readVisibleItemsOnly = false, extension }: Options = {},
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const listQueryKey = getScopedListQueryKey(scope);
  const unreadCountQueryKey = getPublisherUnreadCountQueryKey(scope);
  const listParams = getScopedListParams(scope);

  const listQuery = useInfiniteQuery({
    queryKey: listQueryKey,
    queryFn: async ({ pageParam }) => {
      const page = await listNotifications({
        ...listParams,
        page: pageParam,
        pageSize: NOTIFICATIONS_PAGE_SIZE,
      });

      const cachedReadAtById = new Map(
        (
          queryClient.getQueryData<NotificationsListInfinite>(listQueryKey)
            ?.pages ?? []
        )
          .flatMap((cachedPage) => cachedPage.data)
          .filter((item) => item.readAt)
          .map((item) => [item.id, item.readAt] as const),
      );

      return {
        ...page,
        data: page.data.map((item) =>
          !item.readAt && cachedReadAtById.has(item.id)
            ? { ...item, readAt: cachedReadAtById.get(item.id) ?? null }
            : item,
        ),
      };
    },
    initialPageParam: listParams.page,
    getNextPageParam: (lastPage) =>
      lastPage.meta?.hasNextPage ? (lastPage.meta.page ?? 0) + 1 : undefined,
    enabled,
  });

  const [retryLock, setRetryLock] = useState(false);
  const retryLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // React Query briefly clears `error` during a refetch; sticky ref keeps the
  // error block visible across that gap so the empty state doesn't flash.
  const stickyErrorRef = useRef<typeof listQuery.error>(null);

  if (listQuery.error) stickyErrorRef.current = listQuery.error;

  if (listQuery.isSuccess) stickyErrorRef.current = null;
  const stickyError = stickyErrorRef.current;

  useEffect(
    () => () => {
      if (retryLockTimerRef.current) clearTimeout(retryLockTimerRef.current);
    },
    [],
  );

  const handleRetry = () => {
    setRetryLock(true);

    if (retryLockTimerRef.current) clearTimeout(retryLockTimerRef.current);
    retryLockTimerRef.current = setTimeout(
      () => setRetryLock(false),
      MIN_RETRY_FEEDBACK_MS,
    );
    listQuery.refetch();
  };

  const patchMutation = useNotificationsPatchMutation(scope);

  const showToast = useToast();

  const markAllReadMutation = useMarkNotificationsRead({
    mutation: {
      onMutate: async () => {
        await queryClient.cancelQueries({ queryKey: listQueryKey });

        const previousList =
          queryClient.getQueryData<NotificationsListInfinite>(listQueryKey);
        const now = new Date().toISOString();

        queryClient.setQueryData<NotificationsListInfinite>(
          listQueryKey,
          (current) =>
            updateAllPages(current, (entries) =>
              entries.map((item) =>
                item.readAt ? item : { ...item, readAt: now },
              ),
            ),
        );

        return { previousList };
      },
      onError: (error, _vars, context) => {
        if (context?.previousList) {
          queryClient.setQueryData(listQueryKey, context.previousList);
        }
        const { title, description } = getErrorMessage(error);
        showToast({ title, description, variant: "destructive" });
      },
      onSettled: () => {
        if (queryClient.isMutating() <= 1) {
          invalidateAllNotifications(queryClient);
        }
      },
    },
  });

  const deleteMutation = useDeleteNotification({
    mutation: {
      onMutate: async ({ id }) => {
        await Promise.all([
          queryClient.cancelQueries({ queryKey: listQueryKey }),
          queryClient.cancelQueries({ queryKey: unreadCountQueryKey }),
        ]);

        const previousList =
          queryClient.getQueryData<NotificationsListInfinite>(listQueryKey);
        const previousCount =
          queryClient.getQueryData<ApiResponseUnreadCount>(unreadCountQueryKey);
        const removed = previousList?.pages
          .flatMap((page) => page.data)
          .find((item) => item.id === id);

        queryClient.setQueryData<NotificationsListInfinite>(
          listQueryKey,
          (current) =>
            updateAllPages(current, (entries) =>
              entries.filter((item) => item.id !== id),
            ),
        );

        if (removed && !removed.readAt) {
          queryClient.setQueryData<ApiResponseUnreadCount>(
            unreadCountQueryKey,
            (current) => {
              if (!current?.data) return current;

              return {
                ...current,
                data: { count: Math.max(0, current.data.count - 1) },
              };
            },
          );
        }

        return { previousList, previousCount };
      },
      onError: (error, _vars, context) => {
        if (context?.previousList) {
          queryClient.setQueryData(listQueryKey, context.previousList);
        }

        if (context?.previousCount) {
          queryClient.setQueryData(unreadCountQueryKey, context.previousCount);
        }
        const { title, description } = getErrorMessage(error);
        showToast({ title, description, variant: "destructive" });
      },
      onSettled: () => {
        if (queryClient.isMutating() <= 1) {
          invalidateAllNotifications(queryClient);
        }
      },
    },
  });

  const items: NotificationCenterItem<NotificationResponse>[] = (
    listQuery.data?.pages.flatMap((page) => page.data) ?? []
  ).map((notification) => toCenterItem(notification, extension));

  const handleItemClick = (
    item: NotificationCenterItem<NotificationResponse>,
  ) => {
    if (!item.meta) return;

    const extensionUrl = extension?.getUrl?.(item.meta);
    const url =
      extensionUrl === undefined
        ? dispatchNotification(item.meta)
        : extensionUrl;

    if (url) navigate(url, { state: { notificationId: item.id } });
  };

  return {
    error: stickyError
      ? {
          message: getErrorMessage(
            stickyError as Parameters<typeof getErrorMessage>[0],
          ).description,
        }
      : undefined,
    hasMore: listQuery.hasNextPage,
    isLoading: Boolean(listQuery.isLoading && !listQuery.isFetched),
    isLoadingMore: listQuery.isFetchingNextPage,
    isRetrying:
      (listQuery.isFetching &&
        !listQuery.isLoading &&
        !listQuery.isFetchingNextPage) ||
      retryLock,
    items,
    onDismiss: (id: string) => deleteMutation.mutate({ id }),
    onItemClick: handleItemClick,
    onLoadMore: () => listQuery.fetchNextPage(),
    onMarkAllRead: () =>
      markAllReadMutation.mutate({
        data: {
          filter: buildMarkAllReadFilter(
            readVisibleItemsOnly,
            items.map((item) => item.id),
          ),
        },
      }),
    onMarkRead: (id: string) =>
      patchMutation.mutate({ id, data: { read: true } }),
    onRetry: handleRetry,
    totalCount: listQuery.data?.pages[0]?.meta?.count ?? items.length,
  };
};
